import { useMemo } from "react";
import { useData } from "@hooks/useData";
import Http from "@core/fetch";
import { components } from "@apiSchema";
import { buildHighlightQuery, hasMatches, stripHtmlTags } from "@helpers";

type MenuItem = components["schemas"]["MenuItemViewModel"];

export interface iChainMenuItems {
    chainId: components["schemas"]["PlaceWithMenuItemsViewModel"]['id'];
    chainName: components["schemas"]["PlaceWithMenuItemsViewModel"]['chainName'];
    menuItems: MenuItem[];
}

async function fetchByTerm(term: string): Promise<iChainMenuItems[]> {
    const data: components["schemas"]["PlacesWithMenuItemsViewModel"] = await Http.get(
        'offering chains + items + count',
        { SearchWord: term },
    );

    return (data?.chainsWithMenuItems || []).map(i => ({
        chainId: i.id,
        chainName: i.chainName,
        menuItems: i.menuItems || [],
    }));
}


function getItemKey(item: MenuItem): string {
    return `${stripHtmlTags(item.title ?? '')}|${stripHtmlTags(item.description ?? '')}`;
}

interface iItemBucket {
    item: MenuItem;
    count: number;
}

function countByKey(items: MenuItem[]): Map<string, iItemBucket> {
    const buckets = new Map<string, iItemBucket>();

    items.forEach(item => {
        const key = getItemKey(item);
        const bucket = buckets.get(key);
        if (bucket) bucket.count += 1;
        else buckets.set(key, { item, count: 1 });
    });

    return buckets;
}

function flattenBuckets(buckets: Map<string, iItemBucket>): MenuItem[] {
    const items: MenuItem[] = [];
    buckets.forEach(({ item, count }) => {
        for (let i = 0; i < count; i += 1) items.push(item);
    });
    return items;
}

function mergeByChain(responses: iChainMenuItems[][]): iChainMenuItems[] {
    if (responses.length <= 1) return responses[0] ?? [];

    const chainOrder: number[] = [];
    const chainNames = new Map<number, iChainMenuItems['chainName']>();
    const chainBuckets = new Map<number, Map<string, iItemBucket>>();

    responses.forEach(rows => rows.forEach(row => {
        if (row.chainId == null) return;

        let buckets = chainBuckets.get(row.chainId);
        if (!buckets) {
            buckets = new Map();
            chainBuckets.set(row.chainId, buckets);
            chainNames.set(row.chainId, row.chainName);
            chainOrder.push(row.chainId);
        }

        countByKey(row.menuItems).forEach((incoming, key) => {
            const existing = (buckets as Map<string, iItemBucket>).get(key);
            if (!existing) (buckets as Map<string, iItemBucket>).set(key, { ...incoming });
            else if (incoming.count > existing.count) existing.count = incoming.count;
        });
    }));

    return chainOrder.map(chainId => ({
        chainId,
        chainName: chainNames.get(chainId),
        menuItems: flattenBuckets(chainBuckets.get(chainId) as Map<string, iItemBucket>),
    }));
}

function applyExcludes(rows: iChainMenuItems[], excludes: string[]): iChainMenuItems[] {
    const query = buildHighlightQuery(excludes);
    if (!query) return rows;

    const isExcluded = (item: MenuItem) =>
        hasMatches(stripHtmlTags(item.title ?? ''), query)
        || hasMatches(stripHtmlTags(item.description ?? ''), query);

    return rows
        .map(row => ({ ...row, menuItems: row.menuItems.filter(item => !isExcluded(item)) }))
        .filter(row => row.menuItems.length > 0);
}

async function getChainsMenuItems(includes: string[]): Promise<iChainMenuItems[]> {

    if (!includes.length) return [];

    const responses = await Promise.all(includes.map(fetchByTerm));

    return mergeByChain(responses);
}

const NO_ROWS: iChainMenuItems[] = [];

export default function useChainsMenuItems(includes: string[], excludes: string[]) {

    const result = useData<iChainMenuItems[]>(NO_ROWS, getChainsMenuItems, includes);

    const data = useMemo(() => applyExcludes(result.data, excludes), [result.data, excludes]);

    return { ...result, data };
}
